import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    TextField,
    Button,
    ButtonGroup,
    Container,
    Fade,
    useTheme,
    alpha,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Area,
    AreaChart,
} from 'recharts';
import {
    TrendingUp,
    Assessment,
    DateRange,
    Analytics,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f'];
const TYPE_COLORS = ['#8884d8', '#82ca9d', '#ffc658'];

const Statistics: React.FC = observer(() => {
    const { applicationStore } = useStore();
    const theme = useTheme();
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
        applicationStore.fetchStatistics(today, today);
    }, [applicationStore]);

    const handleDateFilterChange = () => {
        applicationStore.fetchStatistics(
            startDate || undefined,
            endDate || undefined
        );
    };

    const setQuickFilter = (type: 'today' | 'week' | 'month') => {
        const today = new Date();
        let start: Date;

        switch (type) {
            case 'today':
                start = today;
                break;
            case 'week':
                start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                break;
        }

        const startDateStr = start.toISOString().split('T')[0];
        const endDateStr = today.toISOString().split('T')[0];
        setStartDate(startDateStr);
        setEndDate(endDateStr);
        applicationStore.fetchStatistics(startDateStr, endDateStr);
    };


    const MetricCard = ({
                            title,
                            value,
                            subtitle,
                            color = 'primary',
                            icon,
                            percentage
                        }: {
        title: string;
        value: string | number;
        subtitle: string;
        color?: string;
        icon?: React.ReactNode;
        percentage?: number;
    }) => (
        <Card
            sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                }
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography color="textSecondary" variant="body2">
                        {title}
                    </Typography>
                    {icon}
                </Box>
                <Typography variant="h3" fontWeight="bold" color={`${color}.main`}>
                    {value}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {subtitle}
                </Typography>
                {percentage !== undefined && (
                    <Box
                        sx={{
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            color: percentage >= 0 ? 'success.main' : 'error.main'
                        }}
                    >
                        <TrendingUp fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="caption">
                            {percentage >= 0 ? '+' : ''}{percentage}%
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    if (applicationStore.loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        Загрузка статистики...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (applicationStore.error) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error" sx={{ mb: 2 }}>
                    {applicationStore.error}
                </Alert>
            </Container>
        );
    }

    const stats = applicationStore.statistics;

    if (!stats) {
        return (
            <Container maxWidth="xl">
                <Alert severity="info">
                    Нет данных для отображения статистики
                </Alert>
            </Container>
        );
    }

    // Данные для графиков
    const barData = [
        { name: 'Всего заявок', value: stats.total_applications },
        { name: 'Email подтверждено', value: stats.email_verified },
        { name: 'Отправлено в CRM', value: stats.sent_to_crm },
        { name: 'В Mattermost', value: stats.sent_to_mattermost },
    ];

    const statusData = [
        { name: 'Ожидают', value: stats.pending_applications, color: COLORS[2] },
        { name: 'Обрабатываются', value: stats.processing_applications, color: COLORS[0] },
        { name: 'Завершены', value: stats.completed_applications, color: COLORS[1] },
        { name: 'Ошибки', value: stats.failed_applications, color: COLORS[3] },
    ].filter(item => item.value > 0);

    const typeData = [
        { name: 'Общие', value: stats.general_applications, color: TYPE_COLORS[0] },
        { name: 'VIP', value: stats.vip_applications, color: TYPE_COLORS[1] },
        { name: 'Корпоративные', value: stats.corporate_applications, color: TYPE_COLORS[2] },
    ].filter(item => item.value > 0);

    const conversionData = [
        { name: 'Поступило заявок', value: stats.total_applications, fill: '#8884d8' },
        { name: 'Email подтверждено', value: stats.email_verified, fill: '#82ca9d' },
        { name: 'Отправлено в CRM', value: stats.sent_to_crm, fill: '#ffc658' },
        { name: 'Завершено', value: stats.completed_applications, fill: '#ff7300' },
    ];

    const emailVerificationRate = stats.total_applications > 0
        ? ((stats.email_verified / stats.total_applications) * 100).toFixed(1)
        : '0';

    const crmConversionRate = stats.email_verified > 0
        ? ((stats.sent_to_crm / stats.email_verified) * 100).toFixed(1)
        : '0';

    const completionRate = stats.total_applications > 0
        ? ((stats.completed_applications / stats.total_applications) * 100).toFixed(1)
        : '0';

    const getPeriodText = () => {
        if (!startDate && !endDate) return 'Все время';
        if (startDate === endDate) return new Date(startDate).toLocaleDateString('ru-RU');
        if (startDate && endDate) {
            return `${new Date(startDate).toLocaleDateString('ru-RU')} - ${new Date(endDate).toLocaleDateString('ru-RU')}`;
        }
        return 'Выбранный период';
    };

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
        if (percent < 0.05) return null;

        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                fontSize={12}
                fontWeight="bold"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                    Аналитика и статистика
                </Typography>
                <Typography variant="h6" color="textSecondary">
                    Детальный анализ работы с заявками
                </Typography>
            </Box>

            {/* Фильтр по периоду */}
            <Fade in timeout={300}>
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Box display="flex" alignItems="center">
                            <DateRange sx={{ mr: 1 }} />
                            <Typography variant="h6">Период анализа</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                            {getPeriodText()}
                        </Typography>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Дата до"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <Button
                                variant="contained"
                                onClick={handleDateFilterChange}
                                disabled={applicationStore.loading}
                                fullWidth
                            >
                                Применить
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                            <ButtonGroup variant="outlined" size="small">
                                <Button onClick={() => setQuickFilter('today')}>Сегодня</Button>
                                <Button onClick={() => setQuickFilter('week')}>Неделя</Button>
                                <Button onClick={() => setQuickFilter('month')}>Месяц</Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Paper>
            </Fade>

            {/* Карточки с ключевыми метриками */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={400}>
                        <Box>
                            <MetricCard
                                title="Подтверждение Email"
                                value={`${emailVerificationRate}%`}
                                subtitle={`${stats.email_verified} из ${stats.total_applications}`}
                                color="primary"
                                icon={<Analytics />}
                                percentage={15}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={500}>
                        <Box>
                            <MetricCard
                                title="Конверсия в CRM"
                                value={`${crmConversionRate}%`}
                                subtitle={`${stats.sent_to_crm} из ${stats.email_verified}`}
                                color="success"
                                icon={<TrendingUp />}
                                percentage={8}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={600}>
                        <Box>
                            <MetricCard
                                title="Завершение заявок"
                                value={`${completionRate}%`}
                                subtitle={`${stats.completed_applications} из ${stats.total_applications}`}
                                color="info"
                                icon={<Assessment />}
                                percentage={-2}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={700}>
                        <Box>
                            <MetricCard
                                title="Уведомления"
                                value={stats.sent_to_mattermost}
                                subtitle="отправлено в Mattermost"
                                color="warning"
                                icon={<Analytics />}
                            />
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Воронка конверсии */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={800}>
                        <Paper sx={{ p: 3, height: 400 }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Воронка конверсии
                            </Typography>
                            <ResponsiveContainer width="100%" height="90%">
                                <AreaChart data={conversionData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        fontSize={12}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Area
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#8884d8"
                                        fill="url(#colorGradient)"
                                    />
                                    <defs>
                                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                                        </linearGradient>
                                    </defs>
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Распределение по статусам */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={900}>
                        <Paper sx={{ p: 3, height: 400 }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Распределение по статусам
                            </Typography>
                            {statusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="90%">
                                    <PieChart>
                                        <Pie
                                            data={statusData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {statusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    height="90%"
                                >
                                    <Typography color="textSecondary">
                                        Нет данных для отображения
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Fade>
                </Grid>

                {/* Общая статистика обработки */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={1000}>
                        <Paper sx={{ p: 3, height: 400 }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Общая статистика обработки
                            </Typography>
                            <ResponsiveContainer width="100%" height="90%">
                                <BarChart data={barData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        fontSize={12}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Распределение по типам заявок */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={1100}>
                        <Paper sx={{ p: 3, height: 400 }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Типы заявок
                            </Typography>
                            {typeData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="90%">
                                    <PieChart>
                                        <Pie
                                            data={typeData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {typeData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    height="90%"
                                >
                                    <Typography color="textSecondary">
                                        Нет данных для отображения
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Fade>
                </Grid>

                {/* Детальная статистика */}
                <Grid item xs={12}>
                    <Fade in timeout={1200}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Детальная статистика
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Box textAlign="center" py={2}>
                                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                                            {stats.total_applications}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Всего заявок
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box textAlign="center" py={2}>
                                        <Typography variant="h4" color="success.main" fontWeight="bold">
                                            {stats.email_verified}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Email подтверждено
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box textAlign="center" py={2}>
                                        <Typography variant="h4" color="info.main" fontWeight="bold">
                                            {stats.completed_applications}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Успешно завершено
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>
        </Container>
    );
});

export default Statistics;
